/* start - flights_split_bounds_us */
import React, { useCallback } from "react";
import SearchResultsWrapperDesktop from "./SearchResultsWrapper.desktop";
import { HiddenVisually, Stack, Title, useViewport } from "@bookingcom/bui-react";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner";
import useSearchResultsDesktopController from "./useSearchResults.desktop.controller";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop";
import FailedSearchResults from "./FailedSearchResults.desktop";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights";
import { TooManyFilters } from "./components/TooManyFilters";
import { NoResultsFound } from "./components/NoResultsFound";
import SearchResultsContentDesktop from "./SearchResultsContent.desktop";
import SearchResultsListOutboundDesktop from "./SearchResultsList.outbound.desktop";
import { UIFlightData } from "@flights/types";
import useCreateUrl from "../../../hooks/useCreateUrl";
import { useHistory, useParams } from "react-router-dom";
import { t, useI18n } from "@bookingcom/lingojs-react";
import Frame from "../Frame";
import NoDirectFlightsBanner from "./components/NoDirectFlightsBanner";
import { DestinationUkraineWarning } from "../TravelAlert";
import SirfInfoBanner from "../SirfInfoBanner";
import flights_split_bounds_us from "../../../utils/experiments/funnel/flights_split_bounds_us";
import { getUpdatedSearchParams } from "../../../hooks/useSearchFilters";
import { getInitialState } from "../../../store/search/state";
import SearchSortSelector from "./components/SearchSortSelector";
import SearchBaggagePolicies from "../SearchBaggagePolicies";
import { trackWithDefaultStage } from "@flights/et-universal";

const Header = () => {
  const i18n = useI18n();
  const { isSmall } = useViewport();

  const isResponsiveFixesSm = isSmall && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  const title = (
    <Title
      titleTagName="h2"
      title={i18n.trans(t("flights_sr_split_bounds_us_select_departing"))}
      subtitle={i18n.trans(t("flights_sr_split_bounds_us_price_disclaimer"))}
    />
  );

  return (
    <Frame pb={4}>
      <Stack
        direction={isResponsiveFixesSm ? "column" : "row"}
        gap={2}
        alignItems={isResponsiveFixesSm ? "start" : "center"}
      >
        <Stack.Item grow={true}>{title}</Stack.Item>
        <SearchSortSelector isResponsiveFixesSm={isResponsiveFixesSm} />
      </Stack>
    </Frame>
  );
};

const SearchResultsOutboundDesktop = () => {
  const {
    getScreenReaderAnnouncementText,
    appliedFilters,
    isFirstLoad,
    canExtendSearch,
    onExpandToCitySearch,
    hasFlights,
    canRemoveDirectFlightsFilter,
    flights,
    fetchStatus,
    banners,
    isSirfAvailable,
    fetchedAt,
    resetInboundFilters
  } = useSearchResultsDesktopController();
  const i18n = useI18n();

  const humanRightBanner = banners?.find((banner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");

  const { createUrl } = useCreateUrl();
  const history = useHistory();
  const { airports } = useParams<{ airports: string }>();

  const onClickSelectFlight = useCallback(
    async (flight: UIFlightData) => {
      flights_split_bounds_us.goals.cta_click();

      // Whenever we select a new outbound flight, we want to reset the inbound filters.
      // We also use initial values for inbound filters, since the state is not guaranteed
      // to be updated yet.
      resetInboundFilters();
      const params = resetSortAndPageParams(getUpdatedSearchParams(getInitialState().filtersDraftInbound));

      const url = createUrl(`/flights/${airports}/inbound/${flight.outboundFlightInfo?.token}/?${params}`);
      history.push(url);
    },
    [resetInboundFilters, createUrl, airports, history]
  );

  let resultingComp = undefined;

  if (fetchStatus === "initial" || fetchStatus === "loading") {
    if (isFirstLoad) {
      // this is the result of SSR
      return <SkeletonSearchDesktop />;
    }
    // need sidebar from now on and no SSR
    resultingComp = <SkeletonSearchContent />;
  }

  if (fetchStatus === "failed" && canExtendSearch) {
    // this can also be SSR
    return <FailedSearchResults onClickExpandToCitySearch={onExpandToCitySearch} />;
  }

  // case of success should go here, i.e. the composed result
  if (fetchStatus === "success") {
    if (hasFlights) {
      // No SSR for this case, why not ? because api is called from client side
      resultingComp = (
        <SearchResultsContentDesktop flights={flights} fetchedAt={fetchedAt}>
          <NoDirectFlightsBanner />
          <DestinationUkraineWarning />
          {isSirfAvailable && (
            <SirfInfoBanner
              bannerName="search-results"
              bannerContent={i18n.trans(t("flights_sr_spanish_discount_banner"))}
            />
          )}
          <SearchBaggagePolicies hideBaggagePolicies={true} />
          <SearchResultsListOutboundDesktop flights={flights} onSelectFlight={onClickSelectFlight} />
        </SearchResultsContentDesktop>
      );
    } else if (canRemoveDirectFlightsFilter) {
      // can be generated from SSR
      return (
        <>
          <SearchResultBanner banner={humanRightBanner} />
          <RemoveDirectFlights />
        </>
      );
    }
  }

  if (!resultingComp) {
    // this is a fallthrough default when everything else fails
    if (appliedFilters.length > 0) {
      // need sidebar here too
      resultingComp = <TooManyFilters />;
    } else {
      return <NoResultsFound />;
    }
  }

  return (
    <SearchResultsWrapperDesktop
      banners={
        <>
          {humanRightBanner && <SearchResultBanner banner={humanRightBanner} />}
          <Header />
        </>
      }
      showGenAiSummary={true}
    >
      {resultingComp}
      <HiddenVisually>
        <div aria-live="polite">{getScreenReaderAnnouncementText()}</div>
      </HiddenVisually>
    </SearchResultsWrapperDesktop>
  );
};

const resetSortAndPageParams = (params: URLSearchParams) => {
  params.set("sort", "BEST");
  params.delete("page");

  return params;
};

export default React.memo(SearchResultsOutboundDesktop);
/* end - flights_split_bounds_us */
